import React, { useState, useEffect, useContext } from 'react';
// import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js"
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';

import DICOMReader, { IMeshNode } from '@csdental/dicom-reader';

import SecondaryToolbar from './toolbar/SecondaryToolbar';
import TitleBar from './titlebar';
import ToolBar from './toolbar';

import DragArea from './dragarea';
import Workspace from './workspace';
import Snapshot from './toolpanel/Snapshot';
import { kDualViewNameFilters, ToolID, ViewOrientation, LangLocale, AppStatus } from './shared/common';

import { AppContext } from './App';

import TitleBarBackground from '../assets/image/bg.png';
import { buildMesh, isBuccalBite, Mesh } from './model/Mesh';
import galleryModel from './model/GalleryModel';
import meshDataModel from './model/MeshDataModel';
import { readArrayBuffer, pointsToPolygon, vtkUtil } from './shared/vtkUtils';
import { MarginLineModel } from './model/MarginLineModel';
import Util from './shared/util';

const validFormat = ['dcm', 'stl', 'ply'];

interface MainProps {
  setLocale: (locale: string) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'grid',
      height: '100vh',
      gridTemplateColumns: '48px auto',
      gridTemplateRows: '56px auto',
      margin: 0,
      padding: 0,
      backgroundColor: 'rgba(245, 245, 245, 1)',
    },
    titlebar: {
      /* toolbar take one single row */
      gridColumnStart: 1,
      gridColumnEnd: 3,
      backgroundColor: 'white',
      backgroundImage: `url(${TitleBarBackground})`,
      backgroundSize: '100% 100%',
      backgroundPosition: 'center center',
      zIndex: 2,
    },
    workspace: {
      backgroundColor: '#f5f5f5',
      borderRadius: '10px 0 0 10px',
      boxShadow: '-4px 0px 4px rgba(0, 0, 0, 0.25)',
      zIndex: 1,
      overflow: 'hidden',
    },
    secondaryToolbar: {
      backgroundColor: 'transparent',
      position: 'absolute',
      right: '20px',
      top: '70px',
      zIndex: 3,
      pointerEvents: 'none',
    },
    snapshot: {
      backgroundColor: 'transparent',
      position: 'absolute',
      left: '60px',
      top: '275px',
    },
    toolbar: {
      backgroundColor: 'white',
    },
    dragarea: {
      position: 'absolute',
      margin: 'auto',

      borderRadius: 4,
      transition: 'box-shadow 1s',
      zIndex: 1001,
      top: '50%',
      left: '50%',
      transform: `translate(-50%, -50%)`,
    },
  })
);

const Main = ({ setLocale }: MainProps) => {
  const classes = useStyles();
  const [withUpperJaw, setWithUpperJaw] = useState(false);
  const [withLowerJaw, setWithLowerJaw] = useState(false);
  const [fileInfo, setFileInfo] = useState({ format: '', file: null });
  const [patientOrientation, setPatientOrientation] = useState<number[]>([]);

  const {
    meshData,
    setMeshData,
    normalMeshes,
    setNormalMeshes,
    buccalMeshes,
    setBuccalMeshes,
    patient,
    setPatient,
    acqInfo,
    setAcqInfo,
    setViewOrientation,
    setZoomFit,
    setResetCamera,
    setSnapshot,
    snapshotImage,
    setSnapshotImage,
    setTextureMesh,
    currentTool,
    setWithBuccalBite,
    setWithMarginLine,
    setMarginLines,
    setDualView,
    setBusyDlgProp,
    status,
    setStatus,
  } = useContext(AppContext);

  useEffect(() => {
    if (fileInfo.file) {
      handleFile(fileInfo.file);
    }
  }, [fileInfo]);

  useEffect(() => {
    if (!meshDataModel.isLoaded()) {
      parseLocale();
      parseCasePk();
    }
  }, []);

  useEffect(() => {
    if (meshData && meshData.length > 0) {
      parseMeshData();
      // clear after parse, avoid display duplicated meshs
      setMeshData([]);
      setStatus(AppStatus.Ready);
    }
  }, [meshData]);

  useEffect(() => {
    galleryModel.addSnapshot(snapshotImage);
  }, [snapshotImage]);

  useEffect(() => {
    if (withUpperJaw && withLowerJaw) {
      setWithBuccalBite(true);
    }
  }, [withUpperJaw, withLowerJaw]);

  const handleFile = (file: File) => {
    console.log('Processing File:', file);

    const reader = new FileReader();
    reader.onload = function (file) {
      parseBuffer(reader.result as ArrayBuffer, fileInfo.format, fileInfo.file.name);
    };
    reader.readAsArrayBuffer(file);
  };

  const onFileDropped = (files: File[]) => {
    let hasValidFile = false;
    files.forEach((file: File) => {
      console.log('Loading File:', file);
      const extension = file.name.split('.').pop().toLowerCase();
      if (validFormat.includes(extension)) {
        hasValidFile = true;
        setFileInfo({ format: extension, file: file });
      } else {
        alert('File format not supported: ' + file.name);
      }
    });

    if (hasValidFile) {
      setBusyDlgProp({ visible: true, title: 'Import Data' });
      setStatus(AppStatus.Loading);
    }
  };

  const parseLocale = () => {
    const search = window.location.search;
    if (search) {
      const urlParams = new URLSearchParams(search);
      let lang = urlParams.get('hl')?.replace('_', '-');
      if (lang/* && LangLocale[lang]*/) {
        console.log('set language to ', lang);
        setLocale(lang);
      }
    }
  };

  const parseCasePk = async () => {
    const search = window.location.search;
    if (search) {
      const params = new URLSearchParams(search);
      const url = params.get('url');
      const projetFormat = params.get('projectFormat') || 'dcm';
      const title = params.get('title') || '';
      if (url && projetFormat) {
        setBusyDlgProp({ visible: true, title: 'Import Data' });
        setStatus(AppStatus.Loading);
        const buffer = await downloadCase(url);
        if (buffer) {
          parseBuffer(buffer, projetFormat, title);
        }
      }
    }
  };

  const parseBuffer = (buffer: ArrayBuffer, format: string, name: string) => {
    console.log('parsing buffer:', buffer);

    switch (format) {
      case 'ply':
        console.log('open ply file');
        {
          const mesh = readArrayBuffer(buffer, name, 'PLY_FILE');
          setMeshData([mesh]);
        }
        break;

      case 'stl':
        console.log('open stl file');
        {
          const mesh = readArrayBuffer(buffer, name, 'STL_FILE');
          setMeshData([mesh]);
        }
        break;

      case 'dcm':
        {
          const t0 = performance.now();
          const dr = new DICOMReader(buffer);
          const data = dr.extractData();
          const t1 = performance.now();
          const parseDuration = ((t1 - t0) / 1000.0).toFixed(3);
          // appInsights.trackEvent({ name: 'ParseDicom', properties: { parseDuration } })

          const patientName = data.patientName;
          const nameSection = patientName ? patientName.split('^') : ['Unknown', 'Patient'];
          const patientId = data.patientId;
          setPatient({
            lastName: nameSection[0],
            firstName: nameSection[1],
            id: patientId,
          });
          setPatientOrientation(data.patientOrientation);
          setMeshData(data.meshes);
          checkDualView(data.meshes);
          setViewOrientation(ViewOrientation.Front);

          const acqTime = data.acquisitionDateTime;
          const timeFormat = Util.formatStringToDate(acqTime);
          const manufactModeName = data.manufacturerModelName;
          const deviceSerialNumber = data.deviceSerialNumber;
          const acqType = data.acquisitionType;
          const acqSoftwareVersion = data.softwareVersions;
          const ver_strs = acqSoftwareVersion.split('##');
          const ver_result = ver_strs[0] + ' ' + ver_strs[1];
          setAcqInfo({
            acquisitionTime: timeFormat,
            manufactModeName: manufactModeName,
            deviceSerialNum: deviceSerialNumber,
            acquisitionType: acqType,
            acquisitionSoftwareVersion: ver_result,
          });
        }
        break;

      default:
        break;
    }
  };

  const checkDualView = (meshes: IMeshNode[]) => {
    let hasDualView = false;
    meshes.forEach((mesh) => {
      kDualViewNameFilters.forEach((nameFilter) => {
        hasDualView = hasDualView || mesh.meshPart.includes(nameFilter);
      });
    });
    setDualView(hasDualView);
  };

  const parseMeshData = () => {
    meshData.forEach((mesh: IMeshNode) => {
      buildMesh(mesh).then((tmpMesh: Mesh) => {
        if (isBuccalBite(mesh.meshPart)) {
          console.log('add buccal mesh', tmpMesh, 'to', buccalMeshes);
          setBuccalMeshes((oldarray) => [...oldarray, tmpMesh]);
          setWithBuccalBite(true);
        } else {
          console.log('add normal mesh', tmpMesh, 'to', normalMeshes);
          vtkUtil.applyPatientOrientation(vtkUtil.getPolyData(tmpMesh?.actor), patientOrientation);
          setNormalMeshes((oldarray) => [...oldarray, tmpMesh]);
          setTextureMesh(mesh.texture != null);
          if (tmpMesh.isUpperJaw()) {
            setWithUpperJaw(true);
          }
          if (tmpMesh.isLowerJaw()) {
            setWithLowerJaw(true);
          }
        }
      });
    });
    const withMarginLine = meshData.some((mesh) => mesh.marginLines && mesh.marginLines.length > 0);
    if (withMarginLine) {
      const marginLineModelList: Array<MarginLineModel> = [];
      meshData.forEach((mesh: IMeshNode) => {
        if (mesh.marginLines && mesh.marginLines.length > 0) {
          mesh.marginLines.forEach((m) => {
            const pt3ds = m.points.filter((pt, index, array) => {
              const findex = array.findIndex((fpt) => {
                return fpt[0] == pt[0] && fpt[1] == pt[1] && fpt[2] == pt[2];
              });
              return findex === index;
            });
            const polydata = pointsToPolygon(pt3ds);
            vtkUtil.applyPatientOrientation(polydata, patientOrientation);
            const model: MarginLineModel = {
              polydata,
              name: m.name,
              meshName: mesh.meshPart,
              arch: mesh.meshPart,
            };
            marginLineModelList.push(model);
          });
        }
      });
      setMarginLines(marginLineModelList);
    }
    setWithMarginLine(withMarginLine);
    setBusyDlgProp({ visible: false, title: '' });
  };

  function checkStatus(response: Response) {
    if (response.ok) {
      meshDataModel.setLoaded(true);
    } else {
      setStatus(AppStatus.Error);
      console.log('failed to download case:', response.statusText);
    }
    return response;
  }

  const downloadCase = async (url: string): Promise<ArrayBuffer> => {
    console.log('Downloading case:', url);
    return fetch(url).then((response) => checkStatus(response) && response.arrayBuffer());
  };

  const isSecondaryVisible = () => {
    return ToolID.Gallery !== currentTool;
  };

  return (
    <>
      <CssBaseline />

      {
        /* show drag & drop area */
        status === AppStatus.Idle && (
          <div className={classes.dragarea}>
            <DragArea onFileDropped={onFileDropped} />
          </div>
        )
      }

      <div className={classes.snapshot}>
        <Snapshot
          source={snapshotImage}
          onVisible={(v: boolean) => {
            if (!v) {
              // clear snapshot image
              setSnapshotImage('');
            }
          }}
        />
      </div>

      {isSecondaryVisible() && (
        <div className={classes.secondaryToolbar}>
          <SecondaryToolbar
            onViewOrientationChange={(val: ViewOrientation) => {
              setViewOrientation(val);
            }}
            onZoomFit={() => {
              setZoomFit(true);
            }}
            onReset={() => {
              setResetCamera(true);
            }}
            onSnapshot={() => {
              setSnapshot(true);
            }}
          />
        </div>
      )}

      <div className={classes.root}>
        <div className={classes.titlebar}>
          <TitleBar firstName={patient.firstName} lastName={patient.lastName} />
        </div>

        <div className={classes.toolbar}>
          <ToolBar></ToolBar>
        </div>

        <div className={classes.workspace}>
          <Workspace />
        </div>
      </div>
    </>
  );
};

export { Main };
